/**
 * ProductPicker — reusable unified product selector for Procurement,
 * Finance, CRM and RFQ flows. Mirrors the API of MasterCounterpartyPicker.
 */

import React, { useEffect, useMemo, useRef, useState } from 'react';
import axios from 'axios';
import { Search, Package, Loader2, X } from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;
const ENDPOINT = `${API_URL}/api/products`;

const ProductPicker = ({ value, onSelect, disabled = false, placeholder = 'Search products by SKU or name…' }) => {
  const [query, setQuery] = useState('');
  const [open, setOpen] = useState(false);
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(false);
  const [selected, setSelected] = useState(null);
  const wrapRef = useRef(null);

  // Load full record when we only have the id
  useEffect(() => {
    if (!value) { setSelected(null); return; }
    if (selected?.id === value) return;
    const t = localStorage.getItem('admin_token');
    axios.get(`${ENDPOINT}/${value}`, { headers: { Authorization: `Bearer ${t}` } })
      .then((r) => setSelected(r.data))
      .catch(() => setSelected(null));
  }, [value, selected?.id]);

  // Debounced search
  useEffect(() => {
    if (!open) return;
    const id = setTimeout(async () => {
      setLoading(true);
      try {
        const t = localStorage.getItem('admin_token');
        const r = await axios.get(`${ENDPOINT}/`, {
          params: { search: query, limit: 25 },
          headers: { Authorization: `Bearer ${t}` },
        });
        setRows(r.data?.rows || []);
      } catch (_) {
        setRows([]);
      } finally {
        setLoading(false);
      }
    }, 250);
    return () => clearTimeout(id);
  }, [query, open]);

  // Close on outside click
  useEffect(() => {
    const handle = (e) => {
      if (wrapRef.current && !wrapRef.current.contains(e.target)) setOpen(false);
    };
    document.addEventListener('mousedown', handle);
    return () => document.removeEventListener('mousedown', handle);
  }, []);

  const pick = (row) => {
    setSelected(row);
    onSelect?.(row);
    setOpen(false);
    setQuery('');
  };

  const clear = (e) => {
    e.stopPropagation();
    setSelected(null);
    onSelect?.(null);
  };

  const label = useMemo(() => {
    if (!selected) return null;
    return (
      <span className="flex items-center gap-2 truncate">
        <Package className="w-3.5 h-3.5 text-[#023E8A]" />
        <span className="font-medium">{selected.sku}</span>
        <span className="text-[#6C757D] truncate">· {selected.name}</span>
      </span>
    );
  }, [selected]);

  return (
    <div ref={wrapRef} className="relative" data-testid="product-picker">
      <button
        type="button"
        disabled={disabled}
        onClick={() => setOpen((v) => !v)}
        className="w-full flex items-center justify-between gap-2 px-3 py-2 border rounded-lg text-sm bg-white hover:border-[#023E8A] disabled:opacity-50"
        data-testid="product-picker-trigger"
      >
        <span className="truncate flex-1 text-left">
          {selected ? label : <span className="text-slate-400">{placeholder}</span>}
        </span>
        {selected ? (
          <span
            onClick={clear}
            role="button"
            tabIndex={0}
            onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') clear(e); }}
            className="p-0.5 hover:bg-slate-100 rounded cursor-pointer"
            data-testid="product-picker-clear"
          >
            <X className="w-3.5 h-3.5 text-[#6C757D]" />
          </span>
        ) : (
          <Search className="w-4 h-4 text-slate-400" />
        )}
      </button>
      {open && (
        <div className="absolute z-50 top-full mt-1 left-0 right-0 bg-white border rounded-lg shadow-lg max-h-80 overflow-hidden flex flex-col">
          <div className="p-2 border-b">
            <input
              autoFocus
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              placeholder="SKU, name, HS code…"
              className="w-full px-2 py-1.5 border rounded text-sm"
              data-testid="product-picker-input"
            />
          </div>
          <div className="overflow-y-auto">
            {loading && <div className="p-3 text-xs text-slate-500 flex items-center gap-1"><Loader2 className="w-3 h-3 animate-spin" />Searching…</div>}
            {!loading && rows.length === 0 && <div className="p-3 text-xs text-slate-400">No products found.</div>}
            {!loading && rows.map((r) => (
              <button
                key={r.id}
                onClick={() => pick(r)}
                className="w-full text-left px-3 py-2 hover:bg-slate-50 border-b border-slate-100 last:border-0"
                data-testid={`product-picker-row-${r.id}`}
              >
                <div className="flex items-center gap-2">
                  <Package className="w-3.5 h-3.5 text-[#023E8A] shrink-0" />
                  <span className="font-mono text-xs font-semibold text-[#023E8A]">{r.sku}</span>
                  <span className="text-sm text-[#2B2D42] truncate flex-1">{r.name}</span>
                </div>
                {r.hs_code && <div className="text-[10px] text-slate-500 mt-0.5">HS {r.hs_code} · {r.unit_of_measure}</div>}
              </button>
            ))}
          </div>
        </div>
      )}
    </div>
  );
};

export default ProductPicker;
